/**
 * Inline editor for a class's descriptive fields (name / grade / section).
 *
 * 2026-09-13: class names were read-only everywhere — there was no update
 * endpoint — so a class created as "G7" could never be renamed. Backed by
 * PATCH /api/school/classes/:id, which the principal, group admin or the
 * class's homeroom teacher may call (same rule as enrolling students).
 *
 * Used in two places: the class detail header (replaces the <h1> while
 * editing) and the school-admin class list (replaces the row).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useUpdateClass } from "../../api/school";

export interface ClassEditFormProps {
  classId: string;
  initial: { name: string; grade: string; section?: string | null };
  onDone: () => void;
}

export function ClassEditForm({ classId, initial, onDone }: ClassEditFormProps) {
  const { t } = useTranslation();
  const update = useUpdateClass(classId);
  const [name, setName] = useState(initial.name);
  const [grade, setGrade] = useState(initial.grade);
  const [section, setSection] = useState(initial.section ?? "");
  const [err, setErr] = useState<string | null>(null);
  const showSection = initial.section !== undefined;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setErr(t("classes.createModal.errName"));
    if (!grade.trim()) return setErr(t("classes.edit.errGrade"));
    setErr(null);
    try {
      await update.mutateAsync({
        name: name.trim(),
        grade: grade.trim(),
        ...(showSection ? { section: section.trim() || null } : {}),
      });
      onDone();
    } catch (ex) {
      setErr(t("classes.edit.failed", { msg: (ex as Error).message }));
    }
  };

  const input = "px-2 py-1.5 rounded border border-slate-200 text-sm";
  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-2" data-testid="class-edit-form">
      <label className="text-sm">
        <span className="block text-xs text-slate-600 mb-1">{t("classes.createModal.name")}</span>
        <input autoFocus type="text" value={name} onChange={(e) => setName(e.target.value)} className={`${input} w-48`} />
      </label>
      <label className="text-sm">
        <span className="block text-xs text-slate-600 mb-1">{t("classes.createModal.grade")}</span>
        <input type="text" value={grade} onChange={(e) => setGrade(e.target.value)} className={`${input} w-20`} placeholder="G7" />
      </label>
      {showSection && (
        <label className="text-sm">
          <span className="block text-xs text-slate-600 mb-1">{t("classes.createModal.section")}</span>
          <input type="text" value={section} onChange={(e) => setSection(e.target.value)} className={`${input} w-16`} placeholder="A" />
        </label>
      )}
      <button
        type="submit"
        disabled={update.isPending}
        className="px-3 py-1.5 text-sm font-medium rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
      >
        {update.isPending ? t("common.saving") : t("common.save")}
      </button>
      <button type="button" onClick={onDone} className="px-3 py-1.5 text-sm rounded border border-slate-200 text-slate-600 hover:bg-slate-50">
        {t("common.cancel")}
      </button>
      {err && <p className="w-full text-xs text-rose-600">{err}</p>}
    </form>
  );
}
